import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { StagePage } from '@/components/generals/stage-page'
import { StageHeading } from '@/components/generals/stage-heading'
import { ContactForm } from '@/components/contact/contact-form'

import { CONTACT, STUDIO_INFO } from '@/constants/studio'

const Contact: FC = (): ReactNode => {
    return (
        <>
            <Seo
                title='Contact'
                description='Book a sitting at Amour, a portrait studio in Lisbon. Tell us what the picture is for and we will send dates and a colour.'
            />

            <StagePage active='/contact'>
                <section
                    id='hero'
                    className='mx-auto max-w-[1200px] px-6 pt-20 pb-14 sm:px-10 sm:pt-28 lg:pt-36'
                >
                    <div className='animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-700 ease-out motion-reduce:animate-none'>
                        <StageHeading
                            level={1}
                            label={CONTACT.label}
                            small={CONTACT.headSmall}
                            large={CONTACT.headLarge}
                        />
                    </div>

                    <p className='mt-10 max-w-[58ch] border-t border-white/10 pt-8 text-[15px] leading-[1.75] text-[#F4F1EA]/70 sm:text-base'>
                        {CONTACT.lede}
                    </p>
                </section>

                <section
                    id='form'
                    className='mx-auto max-w-[1200px] px-6 pb-20 sm:px-10 sm:pb-28'
                >
                    <div className='grid gap-12 lg:grid-cols-[0.75fr_1.25fr] lg:gap-16'>
                        <div className='lg:sticky lg:top-28 lg:self-start'>
                            <h2 className='text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#F4F1EA]/45'>
                                The studio
                            </h2>

                            <dl className='mt-8 divide-y divide-white/10 border-y border-white/10'>
                                <div className='py-5'>
                                    <dt className='text-[10px] uppercase tracking-[0.28em] text-[#F4F1EA]/40'>
                                        Address
                                    </dt>
                                    <dd className='mt-3 text-[14px] leading-relaxed text-[#F4F1EA]/80'>
                                        {STUDIO_INFO.address.map((line) => (
                                            <span key={line} className='block'>
                                                {line}
                                            </span>
                                        ))}
                                    </dd>
                                </div>

                                <div className='py-5'>
                                    <dt className='text-[10px] uppercase tracking-[0.28em] text-[#F4F1EA]/40'>
                                        Email
                                    </dt>
                                    <dd className='mt-3 text-[14px] text-[#F4F1EA]/80'>
                                        <a
                                            href={`mailto:${STUDIO_INFO.email}`}
                                            className='underline-offset-4 transition-colors hover:text-[#F4F1EA] hover:underline'
                                        >
                                            {STUDIO_INFO.email}
                                        </a>
                                    </dd>
                                </div>

                                <div className='py-5'>
                                    <dt className='text-[10px] uppercase tracking-[0.28em] text-[#F4F1EA]/40'>
                                        Telephone
                                    </dt>
                                    <dd className='mt-3 text-[14px] text-[#F4F1EA]/80'>
                                        <a
                                            href={`tel:${STUDIO_INFO.phone.replace(/\s/g, '')}`}
                                            className='underline-offset-4 transition-colors hover:text-[#F4F1EA] hover:underline'
                                        >
                                            {STUDIO_INFO.phone}
                                        </a>
                                    </dd>
                                </div>

                                <div className='py-5'>
                                    <dt className='text-[10px] uppercase tracking-[0.28em] text-[#F4F1EA]/40'>
                                        Open
                                    </dt>
                                    <dd className='mt-3 text-[14px] text-[#F4F1EA]/80'>
                                        {STUDIO_INFO.hours}
                                    </dd>
                                </div>
                            </dl>

                            <ul className='mt-7 flex flex-wrap gap-2'>
                                {STUDIO_INFO.social.map((link) => (
                                    <li key={link.label}>
                                        <a
                                            href={link.href}
                                            className='inline-flex items-center gap-1.5 rounded-full bg-white/[0.09] px-3.5 py-1.5 text-[11px] tracking-[0.04em] text-[#F4F1EA]/75 transition-colors duration-300 hover:bg-white/[0.16] hover:text-[#F4F1EA] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4EA3FF]'
                                        >
                                            {link.label}
                                            <ArrowUpRight className='size-3' aria-hidden />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <ContactForm />
                    </div>
                </section>

                <section id='archive' className='border-t border-white/10'>
                    <div className='mx-auto flex max-w-[1200px] flex-col gap-8 px-6 py-16 sm:px-10 sm:py-20 lg:flex-row lg:items-end lg:justify-between'>
                        <StageHeading
                            small='Not ready to write yet?'
                            large='Look at the archive first'
                        />

                        <Link
                            href='/'
                            className='inline-flex w-fit items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.06] px-6 py-3 text-[12px] uppercase tracking-[0.2em] transition-colors duration-300 hover:border-white/40 hover:bg-white/[0.12] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4EA3FF]'
                        >
                            Open the archive
                            <ArrowUpRight className='size-4' aria-hidden />
                        </Link>
                    </div>
                </section>
            </StagePage>
        </>
    )
}

export default Contact
